import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Plus, TrashSimple, PencilSimple, Envelope, Copy } from "@phosphor-icons/react";

const empty = {
  email: "", password: "", name: "", role: "employee",
  employee_id: "", department: "", position: "",
  store_id: "", supervisor_id: "", shift_id: "",
  employment_status: "active",
  phone: "", whatsapp_opt_in: false,
};

const emptyInvite = { email: "", name: "", role: "employee", store_id: "", shift_id: "", supervisor_id: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState(emptyInvite);
  const [lastInviteLink, setLastInviteLink] = useState("");

  const load = async () => {
    try {
      const [u, s, sh, inv] = await Promise.all([
        api.get("/users"),
        api.get("/stores"),
        api.get("/shifts"),
        api.get("/invites").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data);
      setStores(s.data);
      setShifts(sh.data);
      setInvites(inv.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const supervisors = users.filter((u) => u.role === "supervisor" || u.role === "admin");

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (editingId) {
        delete payload.email;
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editingId}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }
      setForm(empty);
      setEditingId(null);
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      email: u.email,
      password: "",
      name: u.name || "",
      role: u.role || "employee",
      employee_id: u.employee_id || "",
      department: u.department || "",
      position: u.position || "",
      store_id: u.store_id || "",
      supervisor_id: u.supervisor_id || "",
      shift_id: u.shift_id || "",
      employment_status: u.employment_status || "active",
      phone: u.phone || "",
      whatsapp_opt_in: !!u.whatsapp_opt_in,
    });
    setShowForm(true);
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...inviteForm };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const { data } = await api.post("/invites", payload);
      const link = `${window.location.origin}/invite/${data.token}`;
      setLastInviteLink(link);
      try { await navigator.clipboard.writeText(link); } catch {}
      toast.success("Invite created — link copied to clipboard");
      setInviteForm(emptyInvite);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite?")) return;
    try {
      await api.delete(`/invites/${id}`);
      toast.success("Invite revoked");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const copyLink = async (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Invite link copied"); }
    catch { toast.error("Copy failed — link: " + link); }
  };

  return (
    <div className="px-4 md:px-10 py-8 max-w-7xl mx-auto" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Personnel</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mt-1">Users & Invites</h1>
          <p className="text-zinc-600 mt-1 text-sm">{users.length} users · {invites.filter((i) => !i.used_at).length} pending invites.</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="invite-btn"
            onClick={() => { setShowInvite((v) => !v); setShowForm(false); }}
            className="flex items-center gap-2 px-4 py-2.5 border border-zinc-300 hover:bg-zinc-50 text-sm font-semibold rounded-sm"
          >
            <Envelope size={14} weight="bold" />
            Invite by email
          </button>
          <button
            data-testid="new-user-btn"
            onClick={() => { setEditingId(null); setForm(empty); setShowForm((v) => !v); setShowInvite(false); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold rounded-sm"
          >
            <Plus size={14} weight="bold" />
            New User
          </button>
        </div>
      </div>

      {showInvite && (
        <form onSubmit={submitInvite} className="bg-white border border-zinc-200 rounded-sm p-5 mb-6 grid sm:grid-cols-3 gap-4" data-testid="invite-form">
          <div className="sm:col-span-3 label-eyebrow">New invite</div>
          <Field label="Email">
            <input type="email" required value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} className="lom-input" data-testid="invite-email-input" />
          </Field>
          <Field label="Name (optional)">
            <input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} className="lom-input" />
          </Field>
          <Field label="Role">
            <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })} className="lom-input" data-testid="invite-role-select">
              <option value="employee">Employee</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Store (optional)">
            <select value={inviteForm.store_id || ""} onChange={(e) => setInviteForm({ ...inviteForm, store_id: e.target.value })} className="lom-input">
              <option value="">— none —</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Shift (optional)">
            <select value={inviteForm.shift_id || ""} onChange={(e) => setInviteForm({ ...inviteForm, shift_id: e.target.value })} className="lom-input">
              <option value="">— none —</option>
              {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Supervisor (optional)">
            <select value={inviteForm.supervisor_id || ""} onChange={(e) => setInviteForm({ ...inviteForm, supervisor_id: e.target.value })} className="lom-input">
              <option value="">— none —</option>
              {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          {lastInviteLink && (
            <div className="sm:col-span-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-sm text-sm flex items-center justify-between gap-3">
              <div className="font-mono text-xs truncate text-emerald-900">{lastInviteLink}</div>
              <button type="button" onClick={() => copyLink(lastInviteLink.split("/invite/")[1])} className="flex items-center gap-1 px-2 py-1 bg-white border border-emerald-300 rounded-sm text-xs font-semibold">
                <Copy size={12} /> Copy
              </button>
            </div>
          )}
          <div className="sm:col-span-3 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 border border-zinc-300 rounded-sm text-sm">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm text-sm" data-testid="invite-submit-btn">
              Generate invite link
            </button>
          </div>
        </form>
      )}

      {invites.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden mb-6" data-testid="invites-table">
          <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
            <div>
              <div className="label-eyebrow">Outstanding</div>
              <h3 className="font-heading font-bold text-lg text-zinc-950">Pending invites</h3>
            </div>
            <div className="text-xs font-mono text-zinc-500">{invites.length} total</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr><Th>Email</Th><Th>Role</Th><Th>Status</Th><Th>Expires</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {invites.map((i) => {
                const isUsed = !!i.used_at;
                const expired = !isUsed && i.expires_at && new Date(i.expires_at) < new Date();
                return (
                  <tr key={i.id} data-testid={`invite-row-${i.id}`}>
                    <Td className="font-mono text-xs">{i.email}</Td>
                    <Td className="capitalize">{i.role}</Td>
                    <Td>
                      <span className={`px-2 py-0.5 rounded-sm text-xs uppercase font-bold tracking-wider border ${
                        isUsed ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : expired ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}>
                        {isUsed ? "accepted" : expired ? "expired" : "pending"}
                      </span>
                    </Td>
                    <Td className="font-mono text-xs">{i.expires_at ? new Date(i.expires_at).toLocaleDateString() : "—"}</Td>
                    <Td className="text-right">
                      <div className="inline-flex gap-1">
                        {!isUsed && (
                          <button onClick={() => copyLink(i.token)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`copy-invite-${i.id}`}><Copy size={14} /></button>
                        )}
                        <button onClick={() => revokeInvite(i.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm" data-testid={`revoke-invite-${i.id}`}><TrashSimple size={14} /></button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-sm p-5 mb-6 grid sm:grid-cols-3 gap-4" data-testid="user-form">
          <div className="sm:col-span-3 label-eyebrow">{editingId ? "Edit user" : "New user"}</div>
          <Field label="Full name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="lom-input" data-testid="user-name-input" />
          </Field>
          <Field label="Email">
            <input type="email" required disabled={!!editingId} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="lom-input" data-testid="user-email-input" />
          </Field>
          <Field label={editingId ? "Password (leave blank to keep)" : "Password"}>
            <input type="password" required={!editingId} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="lom-input" data-testid="user-password-input" />
          </Field>
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="lom-input" data-testid="user-role-select">
              <option value="employee">Employee</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Employee ID"><input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="lom-input" /></Field>
          <Field label="Department"><input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="lom-input" /></Field>
          <Field label="Position"><input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="lom-input" /></Field>
          <Field label="Store">
            <select value={form.store_id || ""} onChange={(e) => setForm({ ...form, store_id: e.target.value })} className="lom-input">
              <option value="">— none —</option>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Shift">
            <select value={form.shift_id || ""} onChange={(e) => setForm({ ...form, shift_id: e.target.value })} className="lom-input">
              <option value="">— none —</option>
              {shifts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Supervisor">
            <select value={form.supervisor_id || ""} onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })} className="lom-input">
              <option value="">— none —</option>
              {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Employment status">
            <select value={form.employment_status} onChange={(e) => setForm({ ...form, employment_status: e.target.value })} className="lom-input">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="terminated">Terminated</option>
            </select>
          </Field>
          <div className="sm:col-span-3 flex gap-2 justify-end">
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border border-zinc-300 rounded-sm text-sm">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm text-sm" data-testid="user-submit-btn">
              {editingId ? "Save changes" : "Create user"}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr><Th>Name</Th><Th>Role</Th><Th>Employee ID</Th><Th>Department</Th><Th>Store</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <Td>
                  <div className="font-semibold text-zinc-950">{u.name}</div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </Td>
                <Td><span className="px-2 py-0.5 rounded-sm text-xs uppercase font-bold tracking-wider bg-zinc-100 text-zinc-800">{u.role}</span></Td>
                <Td className="font-mono text-xs">{u.employee_id || "—"}</Td>
                <Td>{u.department || "—"}</Td>
                <Td>{stores.find((s) => s.id === u.store_id)?.name || "—"}</Td>
                <Td>{u.employment_status}</Td>
                <Td className="text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => startEdit(u)} className="p-1.5 hover:bg-zinc-100 rounded-sm" data-testid={`edit-user-${u.id}`}><PencilSimple size={14} /></button>
                    <button onClick={() => remove(u)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-sm" data-testid={`delete-user-${u.id}`}><TrashSimple size={14} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}</label>
    {children}
  </div>
);
const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-zinc-500 ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-zinc-900 ${className}`}>{children}</td>
);
